// Iter 12 (beta blocker) — INSTANT FLEET-WIDE remote kill switch for the Pine→TSL importer.
//
// A tiny remote JSON flag (no new settings system) lets an operator disable the importer for EVERYONE
// instantly, without a redeploy — important because the importer executes generated CUSTOM code. The
// flag is fetched on load + refreshed periodically and cached so the (synchronous) access gate can read
// it. Configure the source via `VITE_PINE_REMOTE_CONFIG_URL` pointing at a JSON like:
//     { "pineImporterKill": false }
// Flip it to `true` to cut all users within one refresh interval.
//
// FAIL-CLOSED: while a configured remote flag is unread (first load pending) or unreadable (network /
// parse error), the importer is treated as DISABLED. The local `localStorage.pine_importer_kill`
// remains as a per-browser dev/emergency override and is checked separately in pineImporterGate.

export interface PineRemoteKillState {
  /** A remote-config URL is configured (VITE_PINE_REMOTE_CONFIG_URL set). */
  configured: boolean;
  /** A fetch attempt has completed (success or failure). */
  loaded: boolean;
  /** The remote flag says the importer is killed. */
  killed: boolean;
  /** The last fetch failed (→ fail closed). */
  error: boolean;
}

let state: PineRemoteKillState = { configured: false, loaded: false, killed: false, error: false };
let started = false;
const subscribers = new Set<() => void>();
function notify(): void { for (const cb of subscribers) { try { cb(); } catch { /* ignore */ } } }

function remoteUrl(): string | undefined {
  try { return import.meta.env.VITE_PINE_REMOTE_CONFIG_URL || undefined; } catch { return undefined; }
}

async function fetchOnce(): Promise<void> {
  const u = remoteUrl();
  if (!u) { state = { configured: false, loaded: true, killed: false, error: false }; notify(); return; }
  try {
    let signal: AbortSignal | undefined;
    try { signal = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout?.(4000); } catch { signal = undefined; }
    const res = await fetch(u, { cache: 'no-store', ...(signal ? { signal } : {}) });
    if (!res.ok) throw new Error('status ' + res.status);
    const json = (await res.json()) as { pineImporterKill?: boolean };
    state = { configured: true, loaded: true, killed: json?.pineImporterKill === true, error: false };
  } catch {
    // Unreadable → fail closed (the gate treats configured+error as disabled).
    state = { configured: true, loaded: true, killed: false, error: true };
  }
  notify();
}

/** Kick off the remote-config fetch + periodic refresh (idempotent). Call once at app start. */
export function ensurePineRemoteConfig(intervalMs = 60_000): void {
  if (started) return; started = true;
  const u = remoteUrl();
  if (!u) { state = { configured: false, loaded: true, killed: false, error: false }; return; }
  // Before the first read resolves: configured but NOT loaded → the gate fails closed.
  state = { configured: true, loaded: false, killed: false, error: false };
  void fetchOnce();
  try { setInterval(() => void fetchOnce(), intervalMs); } catch { /* no timers in some hosts */ }
}

export function getPineRemoteKillState(): PineRemoteKillState { return state; }
export function subscribePineRemoteConfig(cb: () => void): () => void { subscribers.add(cb); return () => subscribers.delete(cb); }

/** Test hook — inject a remote state deterministically (headless tests). */
export function __setPineRemoteKillStateForTest(s: PineRemoteKillState): void { state = s; }
